import { useEffect, useId, useRef, useState } from 'react';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import agentPermissionsCopy from '@/i18n/ns/agent-permissions';
import { formatCount } from '@/i18n/format';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { ErrorState } from '../primitives';
import { WorkspaceIcon } from '../icons';
import { useOptionalWorkspace } from '../context';
import { AccessKeyForm, NewAccessKey } from './access-keys';
import { AddressField } from './address';
import { approvalErrorMessage } from './allowed-well';
import { agentAccessApi } from './api';
import { AppSetup } from './app-setup';
import { Glyph } from './glyphs';
import { GrantRow, nextScopes, scopeOptions, type OptionalScope } from './grant-row';
import { agentErrorMessage, connectedCount, grantState, isLostResponse, withNames } from './format';
import { hashParam } from './hash-param';
import { useNow, useScopedResource } from './use-scoped';
import type { ApprovalMode, CreatedAccessKey, KnowledgeClientGrant } from './types';

// While an agent is authorized but has not read anything yet, the page checks
// for its first successful read; nothing else changes a grant's status.
const POLL_EVERY_MS = 5000;
const POLL_FOR_MS = 10 * 60 * 1000;

function agentRequested() {
  return hashParam('agent');
}

function setupRequested() {
  return hashParam('setup') === 'knowledge-client';
}

type Notice = { text: string; tone: 'ok' | 'error' };

/** Scoped MCP access for external agents: OAuth sign-ins and access keys. */
export function KnowledgeClients() {
  const company = useCompany();
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(connectionsCopy);
  const permissions = useNamespaceCopy(agentPermissionsCopy);
  const locale = useChatInterfaceLocale();
  const knownName = company.data?.company?.name;
  const companyName = knownName ?? copy.agentCompanyFallback;
  const access = useScopedResource(agentAccessApi.list, copy.agentsLoadFailed);
  const { data, scope } = access;
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [keyFormOpen, setKeyFormOpen] = useState(false);
  const [newKey, setNewKey] = useState<CreatedAccessKey | null>(null);
  const [pollTick, setPollTick] = useState(0);
  // The ten-minute wait belongs to the agents waiting now; a newly authorized
  // agent (seen on a focus re-check) starts a fresh wait.
  const pollWindow = useRef<{ ids: string; since: number } | null>(null);
  const previous = useRef<Map<string, KnowledgeClientGrant> | null>(null);
  const section = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const heading = useId();
  const listHeading = useId();
  const clients = (data?.clients ?? []).map((grant) => withNames(grant, copy));
  // An agent on an old address never becomes ready, so nothing waits for it.
  const waitingIds = clients
    .filter((grant) => grantState(grant) === 'waiting')
    .map((grant) => grant.grantId)
    .join(' ');
  const now = useNow(30_000, clients.length > 0);

  useEffect(() => {
    setOpen(null);
    setBusy(null);
    setNotice(null);
    setSetupOpen(false);
    setKeyFormOpen(false);
    setNewKey(null);
    previous.current = null;
    pollWindow.current = null;
  }, [scope]);

  // Announce only real transitions the server reported: a new authorization,
  // and the first successful read that makes an agent Ready.
  useEffect(() => {
    if (!data) return;
    const before = previous.current;
    previous.current = new Map(data.clients.map((grant) => [grant.grantId, grant]));
    if (!before) return;
    const named = data.clients.map((grant) => withNames(grant, copy));
    // A new access key is announced by its own block, not as an authorization.
    const authorized = named.find(
      (grant) =>
        grant.kind === 'oauth' && !before.has(grant.grantId) && grantState(grant) === 'waiting',
    );
    const readied = named.find((grant) => {
      const earlier = before.get(grant.grantId);
      return grantState(grant) === 'ready' && (!earlier || grantState(earlier) !== 'ready');
    });
    if (readied)
      setNotice({
        text: formatCopy(copy.agentNoticeReady, { app: readied.clientName }),
        tone: 'ok',
      });
    else if (authorized)
      setNotice({
        text: formatCopy(copy.agentNoticeAuthorized, { app: authorized.clientName }),
        tone: 'ok',
      });
  }, [data]);

  useEffect(() => {
    if (!waitingIds) {
      pollWindow.current = null;
      return;
    }
    if (pollWindow.current?.ids !== waitingIds)
      pollWindow.current = { ids: waitingIds, since: Date.now() };
    if (Date.now() - pollWindow.current.since >= POLL_FOR_MS) return;
    const owner = access.owner();
    const timer = window.setTimeout(() => {
      void access
        .refresh()
        .catch(() => false)
        .finally(() => {
          if (access.isCurrent(owner)) setPollTick((value) => value + 1);
        });
    }, POLL_EVERY_MS);
    return () => window.clearTimeout(timer);
  }, [waitingIds, pollTick, scope]);

  // A person usually authorizes in their agent and browser, then comes back.
  useEffect(() => {
    const onFocus = () => void access.refresh().catch(() => false);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [scope]);

  // `?setup=knowledge-client` (from Knowledge → "Connect external agents" and
  // the chat card) opens the setup, on arrival or while this page is open.
  const [deepLinked, setDeepLinked] = useState(setupRequested);
  useEffect(() => {
    const onHash = () => {
      if (setupRequested()) setDeepLinked(true);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (!data || !deepLinked) return;
    setDeepLinked(false);
    setSetupOpen(true);
    section.current?.scrollIntoView?.({ block: 'start' });
  }, [data, deepLinked]);

  // `?agent=<grantId>` (an Agent request's "See what it may do", an Agent activity row) opens
  // that agent's row, on arrival or while this page is open. A grant that is gone opens nothing.
  const [wanted, setWanted] = useState(agentRequested);
  useEffect(() => {
    const onHash = () => setWanted(agentRequested());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (!data || !wanted) return;
    setWanted(null);
    if (!data.clients.some((grant) => grant.grantId === wanted)) return;
    setOpen(wanted);
    // After the row has opened.
    setTimeout(() => {
      for (const row of document.querySelectorAll('[data-grant]'))
        if (row.getAttribute('data-grant') === wanted) row.scrollIntoView?.({ block: 'start' });
    }, 0);
  }, [data, wanted]);

  // A switch for each optional scope the server lists for this company (all of
  // the older ones when it lists none); Library access only when listed and
  // within what the company's policy allows now.
  const listed = (option: OptionalScope) =>
    data?.scopes.some(
      (entry) =>
        entry.id === option.id &&
        entry.optional &&
        (!option.listedOnly || entry.withinRights !== false),
    ) === true;
  const offered = scopeOptions(copy, permissions).filter(
    (option) => listed(option) || (!option.listedOnly && !data?.scopes.length),
  );

  const settle = async (owner: number) => {
    await access.refresh().catch(() => false);
    if (access.isCurrent(owner)) setBusy(null);
  };

  const changeScope = async (
    grant: KnowledgeClientGrant,
    option: OptionalScope,
    enable: boolean,
  ) => {
    if (busy) return;
    const app = grant.clientName;
    if (!window.confirm(enable ? option.confirmOn(app, grant.companyName) : option.confirmOff(app)))
      return;
    const owner = access.owner();
    setBusy(grant.grantId);
    setNotice(null);
    try {
      await agentAccessApi.setScopes(grant.grantId, nextScopes(grant, option, enable));
      if (access.isCurrent(owner))
        setNotice({ text: enable ? option.enabled(app) : option.disabled(app), tone: 'ok' });
    } catch (error) {
      if (access.isCurrent(owner))
        setNotice({
          text: isLostResponse(error)
            ? formatCopy(copy.agentScopeUnconfirmed, { app })
            : agentErrorMessage(
                error,
                formatCopy(copy.agentScopeNotChanged, { app }),
                copy.connectingAgentsOff,
                copy,
              ),
          tone: 'error',
        });
    }
    await settle(owner);
  };

  /** Switches a family between asking each time and automatic. Going automatic asks first. */
  const changeMode = async (grant: KnowledgeClientGrant, family: string, mode: ApprovalMode) => {
    if (busy) return;
    const app = grant.clientName;
    if (mode === 'automatic' && !window.confirm(formatCopy(permissions.confirmAutomatic, { app })))
      return;
    const owner = access.owner();
    setBusy(grant.grantId);
    setNotice(null);
    try {
      await agentAccessApi.setApproval(grant.grantId, family, { mode });
      if (access.isCurrent(owner))
        setNotice({
          text: formatCopy(
            mode === 'automatic' ? permissions.modeAutomaticSet : permissions.modeAskSet,
            { app },
          ),
          tone: 'ok',
        });
    } catch (error) {
      if (access.isCurrent(owner))
        setNotice({ text: approvalErrorMessage(error, permissions, app), tone: 'error' });
    }
    await settle(owner);
  };

  const disconnect = async (grant: KnowledgeClientGrant) => {
    if (busy) return;
    const app = grant.clientName;
    const key = grant.kind === 'access_key';
    if (
      !window.confirm(
        formatCopy(key ? copy.agentConfirmRevokeKey : copy.agentConfirmDisconnect, {
          app,
          company: grant.companyName,
        }),
      )
    )
      return;
    const owner = access.owner();
    setBusy(grant.grantId);
    setNotice(null);
    try {
      await agentAccessApi.disconnect(grant.grantId);
      if (access.isCurrent(owner)) {
        setOpen(null);
        setNotice({
          text: formatCopy(key ? copy.agentKeyRevoked : copy.agentDisconnected, { app }),
          tone: 'ok',
        });
        // The row that held focus is about to disappear.
        headingRef.current?.focus();
      }
    } catch (error) {
      if (access.isCurrent(owner))
        setNotice({
          text: isLostResponse(error)
            ? formatCopy(copy.agentDisconnectUnconfirmed, { app })
            : agentErrorMessage(
                error,
                formatCopy(copy.agentDisconnectFailed, { app }),
                copy.connectingAgentsOff,
                copy,
              ),
          tone: 'error',
        });
    }
    await settle(owner);
  };

  const optionalScopes = (data?.scopes ?? []).filter((entry) => entry.optional);
  // The key form offers only what a key may hold; the server refuses the rest.
  const keyIds = new Set((data?.keyScopes ?? []).map((entry) => entry.id));
  const keyOptionalScopes = optionalScopes.filter((entry) => keyIds.has(entry.id));
  // Closing the key form or the new key hands focus back to the button that
  // opened it, which renders again once they are gone.
  const refocusKeyButton = useRef(false);
  const keyButton = (node: HTMLButtonElement | null) => {
    if (!node || !refocusKeyButton.current) return;
    refocusKeyButton.current = false;
    node.focus();
  };
  const closeKey = () => {
    refocusKeyButton.current = true;
    setKeyFormOpen(false);
    setNewKey(null);
  };
  const canCreateKey = Boolean(data?.keyLifetimeDays.length) && !keyFormOpen && !newKey;
  const setupShown = clients.length === 0 || setupOpen;

  return (
    <section
      ref={section}
      className="ws-connection-scope ws-agent-section"
      aria-labelledby={heading}
    >
      <div className="ws-connection-section-label">
        <h2 id={heading} ref={headingRef} tabIndex={-1}>
          {copy.connectedAgentsTitle}
        </h2>
        {data?.enabled && (
          <span>{formatCount(copy.agentsConnectedShort, connectedCount(clients), locale)}</span>
        )}
      </div>
      <p className="ws-connection-audience">
        {knownName
          ? formatCopy(copy.connectedAgentsSubtitle, { company: knownName })
          : copy.connectedAgentsSubtitleAny}{' '}
        {copy.connectedAgentsSuggest}
      </p>
      {/* The live region stays mounted so each new notice is announced. */}
      <div role="status">
        {notice && (
          <p className="ws-agent-notice" data-tone={notice.tone}>
            {notice.text}
          </p>
        )}
      </div>
      {access.disabled ? (
        // Off is a state, not a failure: say it plainly, with nothing to retry.
        <p className="ws-connection-audience" role="status">
          {copy.connectingAgentsOff}
        </p>
      ) : access.error ? (
        <ErrorState
          message={access.error}
          onRetry={() => {
            // The retry button disappears while the list reloads.
            headingRef.current?.focus();
            access.retry();
          }}
        />
      ) : !data ? (
        <p className="ws-agent-loading" role="status">
          {copy.agentsLoading}
        </p>
      ) : !data.enabled ? (
        <p className="ws-connection-audience">{copy.connectingAgentsOff}</p>
      ) : (
        <>
          <div className="ws-agent-address">
            <h3 className="ws-agent-label">{copy.agentsAddressTitle}</h3>
            <AddressField address={data.address} />
            <p className="ws-agent-privacy">
              <Glyph name="lock" size={13} />
              <span>{formatCopy(copy.agentsPrivacy, { company: companyName })}</span>
            </p>
          </div>
          <div className="ws-agent-links">
            {clients.length > 0 && (
              <button
                type="button"
                className="ws-text-button"
                aria-expanded={setupOpen}
                onClick={() => setSetupOpen((value) => !value)}
              >
                <WorkspaceIcon name={setupOpen ? 'close' : 'plus'} size={14} />
                {setupOpen ? copy.agentsSetupHide : copy.agentsSetupAnother}
              </button>
            )}
            {canCreateKey && (
              <button
                ref={keyButton}
                type="button"
                className="ws-text-button"
                onClick={() => setKeyFormOpen(true)}
              >
                <Glyph name="key" size={14} />
                {copy.keyCreateAction}
              </button>
            )}
            {ws && (
              <button
                type="button"
                className="ws-text-button"
                onClick={() => ws.send(copy.agentsHelpStarter)}
              >
                <WorkspaceIcon name="chat" size={14} />
                {copy.agentsHelpInChat}
              </button>
            )}
          </div>
          {/* Right above the list, whose newest row is the key just made. */}
          {newKey ? (
            <NewAccessKey created={newKey} onDone={closeKey} />
          ) : keyFormOpen ? (
            <AccessKeyForm
              optionalScopes={keyOptionalScopes}
              lifetimes={data.keyLifetimeDays}
              companyName={companyName}
              onCancel={closeKey}
              onCreated={(created) => {
                setKeyFormOpen(false);
                // The key and its new row sit together; the setup steps give way.
                setSetupOpen(false);
                setNewKey(created);
                void access.refresh().catch(() => false);
              }}
              onUncertain={() => void access.refresh().catch(() => false)}
            />
          ) : null}
          {setupShown && (
            <AppSetup
              address={data.address}
              onCreateKey={canCreateKey ? () => setKeyFormOpen(true) : undefined}
            />
          )}
          {clients.length > 0 && (
            <>
              <h3 className="ws-agent-label" id={listHeading}>
                {copy.connectedAgentsList}
              </h3>
              <ul className="ws-agent-list" aria-labelledby={listHeading}>
                {clients.map((grant) => (
                  <GrantRow
                    key={grant.grantId}
                    grant={grant}
                    open={open === grant.grantId}
                    busy={busy !== null}
                    working={busy === grant.grantId}
                    now={now}
                    // A key's row offers only what a key may hold (the server refuses the rest).
                    offered={
                      grant.kind === 'access_key'
                        ? offered.filter((option) => keyIds.has(option.id))
                        : offered
                    }
                    described={data.scopes}
                    showSample={!setupShown}
                    onToggle={() =>
                      setOpen((current) => (current === grant.grantId ? null : grant.grantId))
                    }
                    onScope={(option, enable) => void changeScope(grant, option, enable)}
                    onMode={(family, mode) => void changeMode(grant, family, mode)}
                    onNotice={(text, tone) => setNotice({ text, tone })}
                    onApprovalChanged={() => void access.refresh().catch(() => false)}
                    onDisconnect={() => void disconnect(grant)}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
